import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { taskApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { TaskCard } from '@/components/ui/task-card'
import { Skeleton } from '@/components/ui/skeleton'
import type { DashboardTaskResponse, TaskPriority } from '@/types/task'

const TODAY = new Date().toLocaleDateString('id-ID', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const PRIORITY_FILTERS: { value: TaskPriority | ''; label: string }[] = [
  { value: '', label: 'Semua' },
  { value: 'urgent', label: 'Mendesak' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Sedang' },
  { value: 'low', label: 'Rendah' },
]

export default function DashboardPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('')
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const {
    data: allTasks,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboard', 'today'],
    queryFn: () => taskApi.getTodayTasks(),
    staleTime: 0,
    refetchInterval: 60_000,
  })

  // Client-side priority filter
  const filteredTasks = useMemo<DashboardTaskResponse[]>(() => {
    if (!allTasks) return []
    if (!priorityFilter) return allTasks
    return allTasks.filter((t) => t.priority === priorityFilter)
  }, [allTasks, priorityFilter])

  // Group by workspace, preserving insertion order (tasks already sorted by priority)
  const groups = useMemo(() => {
    const map = new Map<string, DashboardTaskResponse[]>()
    for (const task of filteredTasks) {
      const key = task.workspace_slug
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(task)
    }
    return Array.from(map.entries()).map(([slug, tasks]) => ({
      slug,
      name: tasks[0].workspace_name,
      tasks,
    }))
  }, [filteredTasks])

  function toggleGroup(slug: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  const displayName = user?.email ?? 'Pengguna'

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Selamat Datang, {displayName}</h1>
        <p className="text-sm text-muted-foreground capitalize">{TODAY}</p>
      </div>

      {/* Section header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold tracking-tight">Tugas Hari Ini</h2>
          {!isLoading && allTasks && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {filteredTasks.length}
            </span>
          )}
        </div>
      </div>

      {/* Priority filter pills */}
      <div className="flex flex-wrap gap-2">
        {PRIORITY_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setPriorityFilter(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              priorityFilter === f.value
                ? 'border border-transparent bg-secondary text-foreground'
                : 'border border-border text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-6">
          {Array.from({ length: 2 }).map((_, gi) => (
            <div key={gi} className="space-y-3">
              <Skeleton className="h-6 w-40" />
              <div className="grid gap-3 sm:grid-cols-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-destructive">Gagal memuat dashboard. Coba refresh halaman.</p>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <>
          {groups.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center">
              <p className="text-2xl">🎉</p>
              <p className="mt-2 text-sm font-medium">
                {priorityFilter
                  ? 'Tidak ada tugas dengan prioritas ini hari ini.'
                  : 'Tidak ada tugas jatuh tempo hari ini.'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Nikmati harimu atau cek workspace untuk tugas mendatang.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.slug)
                return (
                  <div key={group.slug}>
                    {/* Group header */}
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <button
                        onClick={() => toggleGroup(group.slug)}
                        className="flex items-center gap-2 text-left"
                      >
                        <span
                          className={`text-xs transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                        >
                          ▼
                        </span>
                        <span className="font-semibold tracking-tight">{group.name}</span>
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          {group.tasks.length} tugas
                        </span>
                      </button>
                      <button
                        onClick={() => navigate(`/workspaces/${group.slug}/projects`)}
                        className="shrink-0 text-xs text-primary hover:underline"
                      >
                        Pergi →
                      </button>
                    </div>

                    {/* Task cards */}
                    {!isCollapsed && (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {group.tasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            onClick={() =>
                              navigate(`/workspaces/${group.slug}/projects/${task.project_id}/tasks/${task.id}`)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
